import React, { useEffect, useRef, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { FormProvider, useForm, useWatch } from "react-hook-form";

import {
  ControlledFormCheckbox,
  ControlledFormEditor,
  ControlledFormSwitch,
} from "../../../../components";

import {
  getDynamicModifyContentTagReplacementText,
  getSelectedTextFromRange,
  unwrapElements,
} from "../../../../services/contentUtils";

const DynamicModifyContentTagDialog = ({
  create,
  items = [],
  onClose,
  openState: [open, setOpen],
  readonly,
  tag = {},
}) => {
  const { range, ...formValues } = tag;

  const formMethods = useForm({
    defaultValues: {
      ...formValues,
      items: items.map(({ id }) => (tag.items.includes(id) ? id : false)),
    },
  });
  const { control, getValues } = formMethods;
  const [selectedItems = []] = useWatch({
    control,
    name: ["items"],
  });

  const setReplaceWith = useRef(false);

  const [editor, setEditor] = useState();

  const selectedText = getSelectedTextFromRange(range);

  useEffect(() => {
    const set = Array.from(new Set(selectedItems.filter(Boolean) || []));

    // If there are no items selected, reset the setReplaceWith ref
    if (!set.length) setReplaceWith.current = true;

    // If there is a single item selected and the setReplaceWith ref is set to true
    if (set.length !== 1 || !setReplaceWith.current) return;

    const item = items.find(({ id }) => id === set[0]);
    const element = document.createElement("p");
    element.innerHTML =
      getDynamicModifyContentTagReplacementText(item)(selectedText);
    editor.data.set(element.outerHTML);
    setReplaceWith.current = false;
  }, [selectedItems]);

  const handleCancel = () => {
    setOpen(false);
  };
  const handleClose = () => {
    const contentElement = document.createElement("div");
    contentElement.innerHTML = getValues("replaceWith");
    onClose(create)({
      ...getValues(),
      items: Array.from(new Set(selectedItems.filter(Boolean))),
      range,
      replaceWith: unwrapElements("p")(contentElement).innerHTML,
    });
    setOpen(false);
  };

  return (
    <Dialog maxWidth="md" onClose={handleCancel} open={open}>
      <FormProvider {...formMethods}>
        <DialogTitle>
          {(readonly && "View") || (create ? "Create" : "Update")} Modify Text
          Tag
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            flexDirection: "row",
            maxHeight: "450px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              overflowY: "auto",
            }}
          >
            <AppBar color="transparent" elevation={1} position="static">
              <Typography variant="h5">Items</Typography>
            </AppBar>
            <List sx={{ overflowY: "auto" }}>
              {items.map(({ id, name }, index) => (
                <ListItem dense key={id}>
                  <ListItemIcon>
                    <ControlledFormCheckbox
                      checkboxProps={{ size: "small" }}
                      control={control}
                      defaultValue={selectedItems.includes(id) ? id : false}
                      id={id}
                      label=""
                      name={`items.${index}`}
                      readonly={readonly}
                      value={id}
                    />
                  </ListItemIcon>
                  <ListItemText id={id} primary={name} />
                </ListItem>
              ))}
            </List>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              marginLeft: 2,
              overflowY: "auto",
            }}
          >
            {selectedText && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                  }}
                >
                  <ControlledFormSwitch
                    control={control}
                    label="Highlight"
                    name="highlight"
                    readonly={true}
                  />
                  <ControlledFormSwitch
                    control={control}
                    label="Underline"
                    name="underline"
                    readonly={readonly}
                  />
                </Box>
                <Typography variant="subtitle2">Selected Text</Typography>
                <div
                  dangerouslySetInnerHTML={{
                    __html: selectedText,
                  }}
                  id="selected-content"
                />
              </>
            )}
            <Box sx={{ width: "500px" }}>
              <ControlledFormEditor
                control={control}
                label={selectedText ? "Replacement Text" : "Correct Text"}
                minHeight="230px"
                name="replaceWith"
                onReady={(ed) => {
                  setEditor(ed);
                }}
                options={["embeddedInlineContent"]}
                readonly={readonly}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          {readonly && (
            <Button onClick={handleCancel} variant="text">
              Close
            </Button>
          )}
          {!readonly && (
            <>
              <Button onClick={handleCancel} variant="text">
                Cancel
              </Button>
              <Button
                color="primary"
                disabled={!selectedItems.filter(Boolean).length}
                onClick={handleClose}
                variant="contained"
              >
                {create ? "Create" : "Update"}
              </Button>{" "}
            </>
          )}
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
};

export { DynamicModifyContentTagDialog };
